<div class="module-comparison">
  @if (isLoading()) {
    <app-loading />
  } @else if (hasData()) {
    <div class="module-summary-cards">
      @for (card of summaryCards(); track card.hrToolDirectusId) {
        <article class="module-summary-card">
          <h3 class="module-summary-card-title">{{ card.translatedName }}</h3>
          <div class="module-summary-card-stats">
            <div class="module-summary-card-stat">
              <span class="stat-label">{{
                'project.steps.overallResults.moduleComparison.summary.requiredSupported'
                  | transloco
              }}</span>
              <span class="stat-value stat-value--green">{{ card.requiredFulfilled }}</span>
            </div>
            <div class="module-summary-card-stat">
              <span class="stat-label">{{
                'project.steps.overallResults.moduleComparison.summary.requiredNotSupported'
                  | transloco
              }}</span>
              <span class="stat-value stat-value--red">{{ card.requiredMissing }}</span>
            </div>
            <div class="module-summary-card-stat">
              <span class="stat-label">{{
                'project.steps.overallResults.moduleComparison.summary.extras' | transloco
              }}</span>
              <span class="stat-value stat-value--blue">{{ card.extraSupported }}</span>
            </div>
          </div>
        </article>
      }
    </div>

    <div class="module-table-header">
      <h3 class="module-table-title">
        {{ 'project.steps.overallResults.moduleComparison.table.title' | transloco }}
      </h3>
      <div class="module-table-actions">
        <app-table-expand-controls
          (expandAll)="expandAll()"
          (collapseAll)="collapseAll()"
          [expandAllDisabled]="!hasVisibleModules() || areAllVisibleModulesExpanded()"
          [collapseAllDisabled]="!hasVisibleModules()"
        />
      </div>
    </div>

    <div class="module-table-wrapper">
      <table class="module-table">
        <thead>
          <tr>
            <th class="module-table-col-function">
              {{ 'common.functions' | transloco }}
            </th>
            <th class="module-table-col-status">
              <div class="status-filter-header">
                <span>{{ 'common.status' | transloco }}</span>
                <div class="status-filter-trigger">
                  <p-multiSelect
                    [ngModel]="selectedStatuses()"
                    (ngModelChange)="selectedStatuses.set($event ?? [])"
                    [options]="statusOptions"
                    optionValue="value"
                    [showToggleAll]="false"
                    [filter]="false"
                    [showHeader]="false"
                    [panelStyleClass]="'status-filter-panel'"
                    appendTo="body"
                    class="status-filter-multiselect"
                  >
                    <ng-template #item let-option>
                      <span
                        class="status-badge"
                        [class.status-badge--required]="option.value === 'required'"
                        [class.status-badge--extra]="option.value === 'extra'"
                      >
                        {{ option.labelKey | transloco }}
                      </span>
                    </ng-template>
                  </p-multiSelect>

                  <i class="pi pi-filter status-filter-icon"></i>
                </div>
              </div>
            </th>
            @for (provider of orderedProviders(); track provider.hrToolDirectusId) {
              <th class="module-table-col-provider">
                <div class="provider-filter-header">
                  <span class="provider-header-name">{{ provider.translatedName }}</span>
                  <div class="status-filter-trigger">
                    <p-multiSelect
                      [ngModel]="getProviderSupportFilterValues(provider.hrToolDirectusId)"
                      (ngModelChange)="
                        onProviderSupportFilterChange(provider.hrToolDirectusId, $event)
                      "
                      [options]="supportFilterOptions"
                      optionValue="value"
                      [showToggleAll]="false"
                      [filter]="false"
                      [showHeader]="false"
                      [panelStyleClass]="'status-filter-panel'"
                      appendTo="body"
                      class="status-filter-multiselect"
                    >
                      <ng-template #item let-option>
                        <span class="support-filter-option">
                          <i
                            [class]="option.iconClass"
                            [class.icon-supported]="option.value === 'supported'"
                            [class.icon-missing]="option.value === 'notSupported'"
                          ></i>
                          <span>{{ option.labelKey | transloco }}</span>
                        </span>
                      </ng-template>
                    </p-multiSelect>

                    <i class="pi pi-filter status-filter-icon"></i>
                  </div>
                </div>
              </th>
            }
          </tr>
        </thead>
        <tbody>
          @for (module of filteredModuleRows(); track module.moduleId) {
            @let isExpanded = isModuleExpanded(module.moduleId);
            <tr class="module-row" (click)="toggleModule(module.moduleId)">
              <td class="module-cell">
                <button type="button" class="module-toggle-button">
                  <i
                    class="pi pi-chevron-right module-toggle-icon"
                    [class.module-toggle-icon--expanded]="isExpanded"
                  ></i>
                </button>
                <span class="module-name">{{ module.moduleName }}</span>
              </td>
              <td class="module-count-cell">
                <span class="module-count-label">
                  {{ module.functions.length }} {{ 'common.functions' | transloco }}
                </span>
              </td>
              @for (provider of orderedProviders(); track provider.hrToolDirectusId) {
                <td class="module-value-cell"></td>
              }
            </tr>

            @if (isExpanded) {
              @for (functionRow of module.functions; track functionRow.functionId) {
                <tr class="function-row">
                  <td class="function-cell">{{ functionRow.functionName }}</td>
                  <td class="status-cell">
                    <span
                      class="status-badge"
                      [class.status-badge--required]="functionRow.status === 'required'"
                      [class.status-badge--extra]="functionRow.status === 'extra'"
                    >
                      {{
                        'project.steps.overallResults.moduleComparison.status.' + functionRow.status
                          | transloco
                      }}
                    </span>
                  </td>
                  @for (provider of orderedProviders(); track provider.hrToolDirectusId) {
                    <td class="value-cell">
                      @if (isFunctionSupported(provider.hrToolDirectusId, functionRow)) {
                        <i class="pi pi-check icon-supported"></i>
                      } @else {
                        <i class="pi pi-times icon-missing"></i>
                      }
                    </td>
                  }
                </tr>
              }
            }
          }
        </tbody>
      </table>
    </div>

    <div class="module-legend">
      <h4 class="module-legend-title">
        {{ 'project.steps.overallResults.moduleComparison.legend.title' | transloco }}
      </h4>
      <div class="module-legend-items">
        <div class="module-legend-item">
          <i class="pi pi-check icon-supported"></i>
          <span>{{
            'project.steps.overallResults.moduleComparison.legend.supported' | transloco
          }}</span>
        </div>
        <div class="module-legend-item">
          <i class="pi pi-times icon-missing"></i>
          <span>{{
            'project.steps.overallResults.moduleComparison.legend.notSupported' | transloco
          }}</span>
        </div>
      </div>
    </div>
  } @else {
    <div class="module-comparison-empty">
      <i class="pi pi-info-circle empty-icon"></i>
      <h3 class="empty-title">
        {{ 'project.steps.overallResults.moduleComparison.noData.title' | transloco }}
      </h3>
      <p class="empty-message">
        {{ 'project.steps.overallResults.moduleComparison.noData.message' | transloco }}
      </p>
    </div>
  }
</div>
